import React, { useRef, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useScrollAnimation, fadeInUpVariants, staggerContainerVariants } from '../hooks/useScrollAnimation';
import CTAPhoneMockup from './CTAPhoneMockup';

const CallToActionContainer = styled(motion.div)`
  width: 100%;
  max-width: 100%;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  background: #0A0118;
  color: #fff;
  overflow: hidden;
  min-height: 100vh;
  min-height: 700px;
`;

const BackgroundGradient = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to bottom, #6832DC 0%, #341A78 10%, black 100%, #000000 100%); 
  z-index: 0;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 1200px;
  z-index: 1;
  padding: 0 20px;
  height: 100vh;
  position: relative;
  
  @media (max-width: 768px) {
    height: auto;
    min-height: 100vh;
    padding: 60px 15px;
  }
`;

const HeaderSection = styled.div`
  position: absolute;
  top: 15%;
  left: 0;
  right: 0;
  text-align: center;
  z-index: 3;
  padding: 0 20px;
  
  @media (max-width: 768px) {
    top: 10%;
  }
`;

const Title = styled(motion.h2)`
  font-size: 58px;
  font-family: "Black Han Sans", sans-serif;
  letter-spacing: -0.02em;
  text-transform: uppercase;
  font-weight: 900;
  margin-bottom: 10px;
  color: white;
  position: relative;
  text-align: center;
  top: 30px;
  letter-spacing: -0.02em;
  z-index: 1000900;
  text-shadow: 0 2px 20px rgba(104, 50, 220, 0.6);
  
  @media (max-width: 768px) {
    font-size: 42px;
  }
  
  @media (max-width: 480px) {
    font-size: 24px;
    top: -20px;
  }
`;

const Subtitle = styled(motion.p)`
  font-size: 24px;
  font-weight: 600;
  color: #DCB9FF;
  font-family: "Black Han Sans", sans-serif;
  font-weight: 400;
  font-style: normal;
  text-align: center;
  max-width: 500px;
  margin: 0 auto;
  margin-bottom: 30px;
  letter-spacing: -0.01em;
  
  @media (max-width: 768px) {
    font-size: 18px;
    margin-bottom: 20px;
  }
`;

const PhoneContainer = styled(motion.div)`
  position: relative;
  z-index: 2;
  margin-top: 180px;
  
  @media (max-width: 768px) {
    margin-top: 100px;
  }
`;

const LightSpot = styled.div`
  position: absolute;
  width: 800px;
  height: 800px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(143, 0, 255, 0.15) 0%, rgba(143, 0, 255, 0) 70%);
  z-index: 0;
  
  &.top-left {
    top: -400px;
    left: -400px;
  }
  
  &.bottom-right {
    bottom: -400px;
    right: -400px;
  }
`;

const FloatingParticle = styled.div`
  position: absolute;
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  border-radius: 50%;
  background: ${props => props.color};
  opacity: ${props => props.opacity};
  z-index: 1;
  filter: blur(${props => props.blur}px);
  animation: float ${props => props.duration}s ease-in-out infinite;
  top: ${props => props.top}%;
  left: ${props => props.left}%;
  
  @keyframes float {
    0%, 100% {
      transform: translateY(0) translateX(0);
    }
    50% {
      transform: translateY(${props => props.floatDistance}px) translateX(${props => props.floatDistance/2}px);
    }
  }
`;

const PlayButton = styled(motion.div)`
  position: absolute;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(104, 50, 220, 0.3);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  top: 45%;
  left: 38%;
  transform: translate(-50%, -50%);
  cursor: pointer;
  box-shadow: 0 8px 32px rgba(104, 50, 220, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  
  &::before {
    content: '';
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 15px 0 15px 22px;
    border-color: transparent transparent transparent white;
    margin-left: 5px;
    filter: drop-shadow(0 0 8px rgba(255, 255, 255, 0.5));
  }
  
  &:hover {
    background: rgba(104, 50, 220, 0.5);
    box-shadow: 0 8px 32px rgba(104, 50, 220, 0.6);
  }
`;

const DynamicIsland = styled.div`
  position: absolute;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  width: 120px;
  height: 34px;
  background: #000000;
  border-radius: 20px;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.05);
  
  &::after {
    content: '';
    position: absolute;
    width: 8px;
    height: 8px;
    background: #333;
    border-radius: 50%;
    right: 15px;
  }
  
  &::before {
    content: '';
    position: absolute;
    width: 30px;
    height: 4px;
    background: #333;
    border-radius: 2px;
    left: 15px;
  }
`;

const SideButton = styled.div`
  position: absolute;
  background: #0A0A0A;
  border-radius: 2px;
  z-index: 11;
  
  &.power {
    width: 4px;
    height: 60px;
    right: -0px;
    top: 140px;
    border-left: 1px solid #777;
  }
  
  &.volume-up {
    width: 4px;
    height: 40px;
    left: -0px;
    top: 130px;
    border-right: 1px solid #777;
  }
  
  &.volume-down {
    width: 4px;
    height: 40px;
    left: -0px;
    top: 185px;
    border-right: 1px solid #777;
  }
  
  &.silent-switch {
    width: 5px;
    height: 18px;
    left: -0px;
    top: 80px;
    border-right: 1px solid #777;
    border-radius: 2px;
  }
`;

const PhoneScreen = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 40px;
  // box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.7);
`;

const PhoneFrame = styled.div`
  position: relative;
  padding: 0;
  // transform: scale(0.9);
`;

const GlowEffect = styled.div`
  position: absolute;
  width: 200%;
  height: 200px;
  background: linear-gradient(90deg, #6832DC, #BB6BD9);
  filter: blur(60px);
  opacity: 0.2;
  z-index: -1;
  border-radius: 100%;
  bottom: -120px;
  left: -50%;
`;

const StyledSubtitle = styled(motion.p)`
  font-size: 22px;
  color: #DCB9FF;
  font-weight: 500;
  text-align: center;
  max-width: 600px;
  margin: 0 auto;
  margin-top: 15px;
  letter-spacing: 0.5px;
  line-height: 1.5;
  opacity: 0.9;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const CallToAction = () => {
  const particles = [
    { size: 8, color: '#8000FF', opacity: 0.6, blur: 3, duration: 7, top: 20, left: 20, floatDistance: 20 },
    { size: 15, color: '#B69DFF', opacity: 0.4, blur: 4, duration: 9, top: 70, left: 80, floatDistance: -30 },
    { size: 10, color: '#AA52FF', opacity: 0.5, blur: 2, duration: 8, top: 30, left: 85, floatDistance: 25 },
    { size: 12, color: '#6A00D1', opacity: 0.3, blur: 5, duration: 6, top: 80, left: 15, floatDistance: -20 },
    { size: 6, color: '#8000FF', opacity: 0.7, blur: 1, duration: 10, top: 40, left: 40, floatDistance: 15 },
    { size: 9, color: '#A875FF', opacity: 0.3, blur: 4, duration: 8, top: 65, left: 25, floatDistance: 18 },
    { size: 14, color: '#7B4DFF', opacity: 0.5, blur: 3, duration: 9, top: 15, left: 65, floatDistance: -22 },
    { size: 7, color: '#C79AFF', opacity: 0.6, blur: 2, duration: 11, top: 50, left: 90, floatDistance: 15 },
  ];
  
  const [isPlaying, setIsPlaying] = React.useState(false);
  
  const handlePlayVideo = () => {
    setIsPlaying(!isPlaying);
  };

  return (
    <CallToActionContainer>
      <BackgroundGradient />
      
      <LightSpot className="top-left" />
      <LightSpot className="bottom-right" />
      
      {particles.map((particle, index) => (
        <FloatingParticle key={index} {...particle} />
      ))}
      
      <ContentWrapper>
        <HeaderSection>
          <Title 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
Endless meals made just for you
          </Title>
        </HeaderSection>
        
        <PhoneContainer
          initial={{ opacity: 0, y: 50 }}
          animate={{ 
            opacity: 1, 
            y: 0,
            x: [0, -5, 0, 5, 0],
            rotateZ: [0, -0.5, 0, 0.5, 0]
          }}
          transition={{ 
            y: {
              type: "spring",
              stiffness: 80,
              damping: 15,
              delay: 0.3
            },
            x: {
              duration: 6,
              repeat: Infinity,
              repeatType: "reverse"
            },
            rotateZ: {
              duration: 6,
              repeat: Infinity,
              repeatType: "reverse"
            }
          }}
          whileHover={{ 
            y: -10,
            transition: { duration: 1.5, repeat: Infinity, repeatType: "reverse" }
          }}
        >
          <SideButton className="power" />
          <SideButton className="volume-up" />
          <SideButton className="volume-down" />
          <SideButton className="silent-switch" />
          
          <GlowEffect />
          <PhoneFrame>
            <PhoneScreen>
              <PlayButton 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handlePlayVideo}
                initial={{ opacity: 0 }}
                animate={{ 
                  opacity: 1,
                  scale: isPlaying ? 0 : 1
                }}
                transition={{ 
                  delay: 0.5,
                  type: "spring",
                  stiffness: 400,
                  damping: 15
                }}
              />
              <CTAPhoneMockup 
                videoSrc={require('../assets/video4.mp4')} 
                isPlaying={isPlaying}
              />
            </PhoneScreen>
          </PhoneFrame>
        </PhoneContainer>
      </ContentWrapper>
    </CallToActionContainer>
  );
};

export default CallToAction; 